import gql from 'graphql-tag';
import { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import { Flags } from '@watershed/constants/flags';
import AddIcon from '@watershed/icons/components/Add';
import { getGqlResultData } from '@watershed/shared-frontend/utils/errorUtils';
import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import { routeForPublicApiDocs } from '@watershed/shared-universal/dashboardRoutes';
import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import TextLink from '@watershed/ui-core/components/TextLink';
import useToggle from '@watershed/ui-core/hooks/useToggle';

import {
  GQApiKeyForApiKeyPageFragment,
  GQOauthClientForApiKeyPageFragment,
} from '../../../generated/graphql-operations';
import {
  useApiKeysListQuery,
  useOauthClientsListQuery,
} from '../../../generated/urql';
import { useFeatureFlag } from '../../../utils/FeatureFlag';
import ErrorPage from '../../ErrorPage';
import Page from '../components/core/Page';
import CreateApiKeyStepDialog from '../dialogs/CreateApiKeyStepDialog';
import CreateApiOauthClientDialog from '../dialogs/CreateApiOAuthClientStepDialog';
import ApiKeyGrid from './ApiKeyGrid';
import ApiOauthClientGrid from './ApiOauthClientGrid';

gql`
  fragment OauthClientForApiKeyPage on ExternalOauthClient {
    id
    name
    description
    apiKeyExpiryMs
    roles {
      id
      name
    }
    secretSuffix
    activeApiKeys {
      id
      createdAt
      expiresAt
    }
    createdAt
    updatedAt
  }

  fragment ApiKeyForApiKeyPage on ExternalApiKey {
    id
    name
    description
    createdAt
    updatedAt
    revokedAt
    secret
    roles {
      id
      name
    }
  }

  query OauthClientsList(
    $after: String
    $before: String
    $first: Int
    $last: Int
  ) @withOwner(owner: EnterpriseFoundations) {
    oauthClients(after: $after, before: $before, first: $first, last: $last) {
      edges {
        cursor
        node {
          ...OauthClientForApiKeyPage
        }
      }
    }
  }

  query ApiKeysList($after: String, $before: String, $first: Int, $last: Int)
  @withOwner(owner: EnterpriseFoundations) {
    apiKeys(after: $after, before: $before, first: $first, last: $last) {
      edges {
        cursor
        node {
          ...ApiKeyForApiKeyPage
        }
      }
    }
  }

  mutation DeleteApiKey($input: DeleteApiKeyInput!)
  @withOwner(owner: EnterpriseFoundations) {
    deleteApiKey(input: $input) {
      key {
        id
      }
    }
  }
`;

function ApiKeyPageInner({
  oauthEnabled,
  apiKeys,
  oauthClients,
  toggleCreateApiKeyDialog,
  toggleCreateApiOauthClientDialog,
}: {
  oauthEnabled: boolean;
  apiKeys: Array<GQApiKeyForApiKeyPageFragment>;
  oauthClients: Array<GQOauthClientForApiKeyPageFragment>;
  toggleCreateApiKeyDialog: () => void;
  toggleCreateApiOauthClientDialog: () => void;
}) {
  const [selectedAuthMethod, setSelectedAuthMethod] = useState<
    'token' | 'oauth'
  >(!oauthEnabled || apiKeys.length > 0 ? 'token' : 'oauth');

  const titleText = useMemo(() => {
    return selectedAuthMethod === 'oauth' ? (
      <Trans context="Page title for OAuth API credentials">
        OAuth API credentials
      </Trans>
    ) : (
      <Trans context="Page title for API keys">API keys</Trans>
    );
  }, [selectedAuthMethod]);

  const buttonText = useMemo(() => {
    return selectedAuthMethod === 'oauth' ? (
      <Trans context="Button text for creating OAuth credentials">
        Create credentials
      </Trans>
    ) : (
      <Trans context="Button text for creating API keys">Create key</Trans>
    );
  }, [selectedAuthMethod]);

  const gridStackSx = {
    paddingY: 2,
    paddingX: 1,
    maxHeight: '100%',
    overflowY: 'auto',
  };

  return (
    <Page.Root innerSx={{ gap: 0 }}>
      <Page.Header>
        <Page.TitleContainer>
          <Page.Title>{titleText}</Page.Title>
          <Page.Subtitle>
            <Trans>
              API keys and OAuth credentials let you use Watershed’s public API.
              Both belong to roles, and will have the permissions of the role
              they belong to. For more info on the API and the permissions
              required for specific API routes,{' '}
              <TextLink href={routeForPublicApiDocs()}>
                visit the API docs.
              </TextLink>
            </Trans>
          </Page.Subtitle>
        </Page.TitleContainer>
        <Page.Actions>
          <Button
            data-testid={TestIds.CreateApiKeyButton}
            color="primary"
            startIcon={<AddIcon />}
            onClick={() =>
              selectedAuthMethod === 'oauth'
                ? toggleCreateApiOauthClientDialog()
                : toggleCreateApiKeyDialog()
            }
          >
            {buttonText}
          </Button>
        </Page.Actions>
      </Page.Header>
      {selectedAuthMethod === 'oauth' ? (
        <Stack sx={gridStackSx}>
          <ApiOauthClientGrid
            apiOAuthClients={oauthClients}
            selectedAuthMethod={selectedAuthMethod}
            setSelectedAuthMethod={setSelectedAuthMethod}
            toggleCreateDialog={toggleCreateApiOauthClientDialog}
          />
        </Stack>
      ) : (
        <Stack sx={gridStackSx}>
          <ApiKeyGrid
            apiKeys={apiKeys}
            selectedAuthMethod={selectedAuthMethod}
            setSelectedAuthMethod={setSelectedAuthMethod}
          />
        </Stack>
      )}
    </Page.Root>
  );
}

export default function ApiKeyPage() {
  // Don't paginate these queries for now because we don't expect to have
  // that many OAuth clients or API keys. If we do end up paginating, we
  // need to pass the pagination information to the child components.
  const [authClientsResult] = useOauthClientsListQuery();

  const [apiKeysResult] = useApiKeysListQuery();

  const [createApiKeyDialogOpen, toggleCreateApiKeyDialog] = useToggle(false);
  const [createApiOauthClientDialogOpen, toggleCreateApiOauthClientDialog] =
    useToggle(false);

  const oauthEnabled = useFeatureFlag(Flags.EntFoundEnableOAuth);

  const error = apiKeysResult.error ?? authClientsResult.error;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const oauthClientsData = getGqlResultData(authClientsResult);
  const oauthClients = flattenConnection(oauthClientsData?.oauthClients);

  const apiKeysData = getGqlResultData(apiKeysResult);
  const apiKeys = flattenConnection(apiKeysData?.apiKeys) ?? [];

  // For the initial load, we need to know if we should be showing the api keys or oauth clients
  const isLoading =
    isFetchingOrStale(apiKeysResult) || isFetchingOrStale(authClientsResult);

  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <ApiKeyPageInner
          oauthEnabled={oauthEnabled}
          apiKeys={apiKeys}
          oauthClients={oauthClients}
          toggleCreateApiKeyDialog={toggleCreateApiKeyDialog}
          toggleCreateApiOauthClientDialog={toggleCreateApiOauthClientDialog}
        />
      )}
      <CreateApiKeyStepDialog
        open={createApiKeyDialogOpen}
        onClose={toggleCreateApiKeyDialog}
      />
      <CreateApiOauthClientDialog
        open={createApiOauthClientDialogOpen}
        onClose={toggleCreateApiOauthClientDialog}
      />
    </>
  );
}
